import { useRef } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import cx from 'classnames';

export interface DateStripDay {
  /** `YYYY-MM-DD`. */
  key: string;
  /** Small line above the number: "Lun", "Hoy". */
  top: ReactNode;
  /** The day of the month. */
  main: ReactNode;
  /** Read out instead of the two lines: "lunes 21 de septiembre, 3 citas". */
  label: string;
  /** A dot under the number, e.g. when the day has appointments. */
  marked?: boolean;
  disabled?: boolean;
}

interface DateStripProps {
  days: DateStripDay[];
  selected: string;
  onSelect: (key: string) => void;
  /** Names the group for screen readers. */
  label: string;
  /** Swiping sideways steps to the previous (−1) or next (+1) set of days. */
  onSwipe?: (direction: -1 | 1) => void;
  className?: string;
}

const SWIPE_THRESHOLD = 48;

/**
 * A row of days to tap — the week in the agenda, the next two weeks when
 * booking. Each day is a button showing a short label and the date, with a dot
 * when it has something on it.
 */
export default function DateStrip({
  days,
  selected,
  onSelect,
  label,
  onSwipe,
  className,
}: DateStripProps) {
  const start = useRef<number | null>(null);

  const onPointerDown = (event: PointerEvent) => {
    start.current = event.clientX;
  };
  const onPointerUp = (event: PointerEvent) => {
    if (start.current === null || !onSwipe) return;
    const distance = event.clientX - start.current;
    start.current = null;
    if (Math.abs(distance) >= SWIPE_THRESHOLD) onSwipe(distance < 0 ? 1 : -1);
  };

  return (
    <div
      className={cx('v-datestrip', className)}
      role="group"
      aria-label={label}
      onPointerDown={onSwipe ? onPointerDown : undefined}
      onPointerUp={onSwipe ? onPointerUp : undefined}
    >
      {days.map((day) => (
        <button
          key={day.key}
          type="button"
          className={cx('v-datestrip__day', { 'v-datestrip__day--marked': day.marked })}
          aria-pressed={day.key === selected}
          aria-label={day.label}
          disabled={day.disabled}
          onClick={() => onSelect(day.key)}
        >
          <span className="v-datestrip__top">{day.top}</span>
          <span className="v-datestrip__main">{day.main}</span>
        </button>
      ))}
    </div>
  );
}
